/**
 * 생성기 자료 고르기 — 파일(선택·끌어 놓기)과 링크 입력을 검사해 초안에 붙이고, 검사에 걸리면 그 탭에 안내를 건다.
 * 검사 결과는 통과·거절 모두 quiz_ai_generator_source_select 로 남긴다.
 */
import { trackEvent } from '@zep/logger/tracking';

import { AiFileSizeLimitMb } from '@/shared/features/ai-quiz-generation/consts/fileExtensions';

import { type AiGenerateDockSourceType } from '../consts/aiGenerateDock.const';
import { type AiGenerateDockRunCopy } from '../types/aiGenerateDock.types';
import {
  checkAiGenerateDockFile,
  formatAiGenerateDockFileSize,
  isAiGenerateDockLinkValid,
} from '../utils/aiGenerateDockAttachment';
import { type AiGeneratorAttachMethod } from '../utils/events.quiz-ai-generator';

import { type AiGenerateDockDraft } from './useAiGenerateDockDraft';
import { type AiGenerateDockNotice } from './useAiGenerateDockNotice';

export type AiGenerateDockSourceSelect = {
  /** 파일은 늘 파일 탭에 붙는다 — 끌어다 놓기는 다른 탭에서도 파일 탭으로 옮겨 붙인다. */
  attachFile: (file: File, method: AiGeneratorAttachMethod) => void;
  /** 지금 탭(유튜브·웹사이트)의 링크 칸 글을 자료로 붙인다. */
  submitLink: () => void;
};

export const useAiGenerateDockSourceSelect = ({
  draft,
  sourceType,
  runCopy,
  showNotice,
  clearNotice,
}: {
  draft: AiGenerateDockDraft;
  sourceType: AiGenerateDockSourceType;
  runCopy: AiGenerateDockRunCopy;
} & Pick<
  AiGenerateDockNotice,
  'showNotice' | 'clearNotice'
>): AiGenerateDockSourceSelect => ({
  attachFile: (file, method) => {
    const check = checkAiGenerateDockFile(file);
    trackEvent('quiz_ai_generator_source_select', {
      source_type: 'file',
      method,
      status: check === 'ok' ? 'ready' : 'invalid',
      reason:
        check === 'ok'
          ? ''
          : check === 'too_large'
          ? 'too_large'
          : 'type_not_allowed',
    });
    if (check !== 'ok') {
      showNotice(
        {
          tone: 'destructive',
          message:
            check === 'too_large'
              ? runCopy.fileTooLarge(AiFileSizeLimitMb)
              : runCopy.fileTypeNotAllowed,
        },
        'file',
      );
      return;
    }

    clearNotice();
    draft.setTabSource('file', {
      attachment: {
        kind: 'file',
        status: 'ready',
        name: file.name,
        sizeLabel: formatAiGenerateDockFileSize(file.size),
      },
      source: { kind: 'file', file, fileName: file.name },
    });
  },

  submitLink: () => {
    if (sourceType !== 'youtube' && sourceType !== 'website') return;

    const url = draft.linkUrl.trim();
    const isValid = isAiGenerateDockLinkValid(sourceType, url);
    trackEvent('quiz_ai_generator_source_select', {
      source_type: sourceType,
      method: 'link_submit',
      status: isValid ? 'ready' : 'invalid',
      reason: isValid ? '' : 'link_invalid',
    });
    if (!isValid) {
      showNotice({
        tone: 'destructive',
        message: runCopy.linkInvalid[sourceType],
      });
      return;
    }

    clearNotice();
    draft.setTabSource(sourceType, {
      attachment: { kind: 'link', sourceType, status: 'ready', url },
      source: { kind: 'link', url },
    });
  },
});
